import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Button,
  Switch,
} from "@mui/material";
import { DefaultPrimaryButton, InformationModal } from "@/styles/globalstyles";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useState } from "react";
import { useGetAllUsers, useCreateUser, useUpdateUser } from "@/hooks/userHook";
import { useGetAllGroups } from "@/hooks/groupHook";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import type { User, UserWithPermission } from "@/api/userService";
import { WarningAlert } from "@/utils/alert";
import { getAxiosErrorMessage } from "@/utils/healper";
import CloseButton from "@/components/buttons/CloseButton";
import { checkIsBuyer } from "@/utils/roleUtils";

interface AddUserModalProps {
  open: boolean;
  handleClose: () => void;
  selectedUser: UserWithPermission | null;
}

const SaveUserModal = ({
  open,
  handleClose,
  selectedUser = null,
}: AddUserModalProps) => {
  const [userId, setUserId] = useState<string>("");
  const [user, setUser] = useState<User | null>(null);
  const [isActive, setIsActive] = useState<number>(
    selectedUser && selectedUser.status?.toLocaleLowerCase() === "active"
      ? 1
      : 0,
  );
  const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
  const {
    data: allUsers,
    isFetching: allUsersFetching,
    isError: allUsersIsError,
    refetch: allUsersRefetch,
  } = useGetAllUsers();

  const {
    data: groups,
    isFetching: groupsIsFetching,
    isError: groupsIsError,
  } = useGetAllGroups();

  const {
    mutate: createUserMutate,
    isPending: createUserLoading,
    isError: createUserIsError,
    error: createUserError,
    isSuccess: createUserIsSuccess,
  } = useCreateUser();

  const {
    mutate: updateUserMutate,
    isPending: updateUserLoading,
    isError: updateUserIsError,
    error: updateUserError,
    isSuccess: updateUserIsSuccess,
  } = useUpdateUser();

  useQueryHandler({
    isFetching:
      allUsersFetching ||
      groupsIsFetching ||
      createUserLoading ||
      updateUserLoading,
    isError:
      allUsersIsError ||
      groupsIsError ||
      createUserIsError ||
      updateUserIsError,
    errorMessage:
      createUserIsError || updateUserIsError
        ? `${getAxiosErrorMessage(createUserError || updateUserError)}`
        : "เกิดข้อผิดพลาดในการโหลดข้อมูล",
    error: createUserError || updateUserError,
    isSuccess: createUserIsSuccess || updateUserIsSuccess,
    successMessage: createUserIsSuccess
      ? "เพิ่มผู้ใช้งานสำเร็จ"
      : "อัพเดตผู้ใช้งานสำเร็จ",
  });

  useEffect(() => {
    if (selectedUser === null) {
      setUserId("");
      setUser(null);
      setSelectedGroup(null);
    }
  }, [selectedUser]);

  useEffect(() => {
    if (selectedUser && allUsers && groups) {
      setUserId(selectedUser.user_id);
      const u =
        allUsers?.find((usr) => usr.user_id === selectedUser.user_id) || null;
      setUser(u);
      const userGroups =
        groups
          ?.filter((grp) =>
            selectedUser.user_groups?.includes(grp.display_name),
          )
          .map((grp) => grp.id) || [];
      setSelectedGroup(userGroups[0] || null);
      setIsActive(
        selectedUser.status?.toLocaleLowerCase() === "active" ? 1 : 0,
      );
    }
  }, [selectedUser, allUsers, groups]);

  const handleSearch = async () => {
    let usersData = allUsers;

    if (!allUsers) {
      const result = await allUsersRefetch();
      usersData = result.data;
    }

    const foundUser = usersData?.find((user) => user.user_id === userId);
    setUser(foundUser || null);
    if (!foundUser) {
      WarningAlert("ไม่พบผู้ใช้งาน", "กรุณาตรวจสอบรหัสพนักงานอีกครั้ง");
    }
    if (checkIsBuyer(foundUser)) {
      setSelectedGroup(
        groups?.find((group) => group.group_key === "buyer")?.id || null,
      );
    } else {
      setSelectedGroup(null);
    }
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!selectedGroup) {
      errors.push("กรุณาเลือกบทบาท");
    }

    if (errors.length > 0) {
      WarningAlert(errors.join(", "), "");
      return false;
    }

    return true;
  };

  const handleAddUser = () => {
    if (!user) return;
    if (!validateForm()) return;
    createUserMutate([user.user_id, [selectedGroup!]]);
    handleClose();
    setUserId("");
    setUser(null);
    setSelectedGroup(null);
  };

  const handleEditUser = () => {
    if (!user) return;
    if (!validateForm()) return;
    updateUserMutate({
      user_id: user.user_id,
      is_active: isActive === 1,
      groups: [selectedGroup!],
    });
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ ...InformationModal, maxHeight: "90vh" }}>
        <CloseButton onClick={handleClose} />
        <Typography variant="h2">
          {selectedUser ? "แก้ไขผู้ใช้งาน" : "เพิ่มผู้ใช้งานใหม่"}
        </Typography>
        <Box p={1}>
          <Box mt={1} display="flex" alignItems="center" gap={1.5}>
            <TextField
              fullWidth
              label={
                <>
                  รหัสพนักงาน{" "}
                  <Typography component="span" color="error.main">
                    *
                  </Typography>
                </>
              }
              variant="outlined"
              sx={{ mt: 2 }}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
            <IconButton
              sx={{
                alignItems: "center",
                color: "Info",
                height: "auto",
                mt: 2,
              }}
              aria-label="search"
              onClick={handleSearch}
            >
              <SearchIcon sx={{ fontSize: "1.75rem" }} />
            </IconButton>
          </Box>
        </Box>
        {user && (
          <Box p={1}>
            <Divider sx={{ my: 1 }} />
            <Box mt={1}>
              <TextField
                fullWidth
                label="ชื่อ-นามสกุล"
                variant="outlined"
                sx={{ mt: 2 }}
                value={user ? `${user.name} ${user.lastname}` : ""}
                disabled
              />
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                sx={{ mt: 2 }}
                value={user ? user.email : ""}
                disabled
              />
              <TextField
                fullWidth
                label="แผนก"
                variant="outlined"
                sx={{ mt: 2 }}
                value={user ? user.devision : ""}
                disabled
              />
              <TextField
                fullWidth
                label="ส่วนงาน"
                variant="outlined"
                sx={{ mt: 2 }}
                value={user ? user.department : ""}
                disabled
              />
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel
                  id="group-label"
                  sx={{
                    backgroundColor: "#fff",
                    px: 0.5,
                    color: "#666666",
                    "&.Mui-focused": {
                      color: "primary.main",
                    },
                  }}
                >
                  บทบาท{" "}
                  <Typography component="span" color="error.main">
                    *
                  </Typography>
                </InputLabel>
                <Select
                  labelId="group-label"
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value as number)}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                  }}
                >
                  {checkIsBuyer(user)
                    ? groups
                        ?.filter((group) => group.group_key === "buyer")
                        ?.map((group) => (
                          <MenuItem key={group.id} value={group.id}>
                            <Typography>{group.display_name}</Typography>
                          </MenuItem>
                        ))
                    : groups
                        ?.filter((group) => group.group_key !== "buyer")
                        ?.map((group) => (
                          <MenuItem key={group.id} value={group.id}>
                            <Typography>{group.display_name}</Typography>
                          </MenuItem>
                        ))}
                </Select>
              </FormControl>
              {selectedUser && (
                <Box mt={2} display="flex" alignItems="center" gap={1}>
                  <Switch
                    checked={isActive === 1}
                    onChange={(e) => setIsActive(e.target.checked ? 1 : 0)}
                  />
                  <Typography fontWeight="bold">Active</Typography>
                </Box>
              )}
            </Box>
            <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="contained"
                sx={DefaultPrimaryButton}
                onClick={selectedUser ? handleEditUser : handleAddUser}
              >
                บันทึก
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default SaveUserModal;
